import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { Terminal, LayoutDashboard, Database, Brain, Network, MessageSquare, LogOut, CircleDot, Settings as SettingsIcon, Inbox, Activity, Camera, Target, Briefcase, Wallet, ShieldCheck, Menu, X, Sparkles, Zap, BarChart3, Lock, Clock, AlertTriangle, Globe, Users, Coffee, Award, Star, DollarSign } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import Ticker from "@/components/Ticker";
import OnboardingWizard from "@/components/OnboardingWizard";

// Full "Terminal" navigation — what power users see
const TERMINAL_NAV = [
  { to: "/app", end: true, icon: LayoutDashboard, label: "Overview", test: "nav-overview" },
  { to: "/app/sources", icon: Database, label: "Data Sources", test: "nav-sources" },
  { to: "/app/insights", icon: Brain, label: "DARA Insights", test: "nav-insights" },
  { to: "/app/monetization", icon: Network, label: "Monetization Map", test: "nav-monetization" },
  { to: "/app/cashflow", icon: Wallet, label: "Cash-Flow Forecast", test: "nav-cashflow" },
  { to: "/app/hygiene", icon: ShieldCheck, label: "Xero Hygiene", test: "nav-hygiene" },
  { to: "/app/assistant", icon: MessageSquare, label: "DARA Assistant", test: "nav-assistant" },
  { to: "/app/walkthroughs", icon: Camera, label: "Walkthroughs", test: "nav-walkthroughs" },
  { to: "/app/abm", icon: Target, label: "ABM Pages", test: "nav-abm" },
  { to: "/app/deal-rooms", icon: Briefcase, label: "Deal Rooms", test: "nav-deal-rooms" },
  { to: "/app/settings", icon: SettingsIcon, label: "Settings", test: "nav-settings" },
];

// Industries that get the Commodities desk in their nav
const COMMODITY_INDUSTRIES = new Set(["mining", "shipping", "energy", "resources", "minerals", "metals", "iron ore", "coal", "gold", "copper", "maritime", "logistics", "oil & gas"]);

// Industries that get the POS Live dashboard in their nav (SMB tilt)
const POS_INDUSTRIES = new Set([
  "retail", "hospitality", "consumer goods", "consumer_goods",
  "food & beverage", "f&b", "ecommerce", "e-commerce", "fmcg", "restaurants",
  // Micro-SMB personal services — barbers, salons, food trucks etc.
  "barber shop", "barber", "barbershop", "salon", "hair", "beauty",
  "nail salon", "personal services", "food truck", "cafe", "café",
]);

// Simplified "Simple" mode nav — what the CFO / business owner sees
const SIMPLE_NAV = [
  { to: "/app/today", icon: Sparkles, label: "Today", test: "nav-today" },
  { to: "/app/sources", icon: Database, label: "Data", test: "nav-sources" },
  { to: "/app/settings", icon: SettingsIcon, label: "Settings", test: "nav-settings" },
];

// Phase 25.14a — Micro-SMB nav (tier=micro). A barber doesn't need
// Monetization Maps, ABM Pages or Hedge Ledgers. POS Live is the home screen.
const MICRO_NAV = [
  { to: "/app/pos", icon: Coffee, label: "POS Live", test: "nav-pos-live" },
  { to: "/app/sources", icon: Database, label: "Sources", test: "nav-sources" },
  { to: "/app/assistant", icon: MessageSquare, label: "Ask DARA", test: "nav-assistant" },
  { to: "/app/settings", icon: SettingsIcon, label: "Settings", test: "nav-settings" },
];

const MODE_KEY = "dara_ui_mode";

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [ticker, setTicker] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [workspaceIndustry, setWorkspaceIndustry] = useState("");
  const [workspaceTier, setWorkspaceTier] = useState("enterprise");
  const [mode, setMode] = useState(() => {
    if (typeof window === "undefined") return "terminal";
    return localStorage.getItem(MODE_KEY) || "simple";
  });

  // Inject the Commodity Desk nav item between Cash-Flow and Hygiene
  // when this workspace's industry is mining/shipping/energy related.
  const NAV = useMemo(() => {
    // Phase 25.14a — micro-tier workspaces get a stripped, glance-first nav.
    if (workspaceTier === "micro") return MICRO_NAV;
    const baseNav = mode === "simple" ? SIMPLE_NAV : TERMINAL_NAV;
    const ind = (workspaceIndustry || "").toLowerCase().trim();
    if (mode === "simple") {
      // Simple mode also gets POS Live when the industry is SMB-pos-friendly.
      if (POS_INDUSTRIES.has(ind)) {
        const enriched = [...baseNav];
        const srcIdx = enriched.findIndex(n => n.to === "/app/sources");
        const insertAt = srcIdx >= 0 ? srcIdx + 1 : enriched.length;
        enriched.splice(insertAt, 0, {
          to: "/app/pos", icon: Coffee, label: "POS Live", test: "nav-pos-live",
        });
        return enriched;
      }
      return baseNav;
    }
    if (!COMMODITY_INDUSTRIES.has(ind)) {
      // Industries without commodity exposure but with SMB POS use cases
      // get the POS Live nav. Both can coexist.
      if (POS_INDUSTRIES.has(ind)) {
        const enriched = [...baseNav];
        const cfIdx = enriched.findIndex(n => n.to === "/app/cashflow");
        const insertAt = cfIdx >= 0 ? cfIdx + 1 : enriched.length;
        enriched.splice(insertAt, 0, {
          to: "/app/pos", icon: Coffee, label: "POS Live", test: "nav-pos-live",
        });
        return enriched;
      }
      return baseNav;
    }
    // Splice in after Cash-Flow Forecast (index 4)
    const enriched = [...baseNav];
    const cfIdx = enriched.findIndex(n => n.to === "/app/cashflow");
    const insertAt = cfIdx >= 0 ? cfIdx + 1 : enriched.length;
    enriched.splice(insertAt, 0, {
      to: "/app/commodities", icon: BarChart3, label: "Commodity Desk", test: "nav-commodities",
    });
    enriched.splice(insertAt + 1, 0, {
      to: "/app/hedge-ledger", icon: Lock, label: "Hedge Ledger", test: "nav-hedge-ledger",
    });
    return enriched;
  }, [mode, workspaceIndustry, workspaceTier]);

  const isSimple = mode === "simple";

  const toggleMode = () => {
    const next = mode === "simple" ? "terminal" : "simple";
    setMode(next);
    localStorage.setItem(MODE_KEY, next);
    nav(next === "simple" ? "/app/today" : "/app");
  };

  useEffect(() => {
    api.get("/dashboard/metrics").then(r => setTicker(r.data.ticker || [])).catch(() => {});
  }, [loc.pathname]);

  useEffect(() => {
    api.get("/admin/me").then(r => setIsAdmin(Boolean(r.data.is_super_admin))).catch(() => setIsAdmin(false));
  }, [user?.id]);

  // Phase 36.12 — Trial status (14-day countdown).
  const [trial, setTrial] = useState(null);
  useEffect(() => {
    api.get("/billing/trial-status").then(r => setTrial(r.data)).catch(() => setTrial(null));
  }, [user?.id, loc.pathname]);

  // Fetch the workspace's industry once so we know whether to surface
  // the Commodity Desk in the sidebar.
  useEffect(() => {
    api.get("/workspace").then(r => {
      setWorkspaceIndustry(r.data?.industry || "");
      setWorkspaceTier(r.data?.tier || "enterprise");
    }).catch(() => {});
  }, [user?.id]);

  // In simple mode, auto-redirect from /app index to /app/today.
  // Phase 25.14a — micro-tier tenants land directly on POS Live regardless
  // of mode, since that IS their home screen.
  useEffect(() => {
    if (workspaceTier === "micro" && (loc.pathname === "/app" || loc.pathname === "/app/today")) {
      nav("/app/pos", { replace: true });
      return;
    }
    if (isSimple && loc.pathname === "/app") {
      nav("/app/today", { replace: true });
    }
  }, [isSimple, workspaceTier, loc.pathname, nav]);

  // Close mobile drawer whenever route changes
  useEffect(() => {
    setNavOpen(false);
  }, [loc.pathname]);

  const now = new Date().toISOString().replace("T", " ").slice(0, 19) + "Z";

  const navLinkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 font-mono text-[11px] uppercase tracking-widest transition-colors ${
      isActive
        ? "bg-[#FFB000]/10 text-[#FFB000] border-l-2 border-[#FFB000]"
        : "text-zinc-500 hover:text-white hover:bg-zinc-900/50 border-l-2 border-transparent"
    }`;

  const NavInner = (
    <>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {NAV.map(n => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.test} className={navLinkClass}>
            <n.icon className="w-4 h-4 stroke-[1.5]" />
            {n.label}
          </NavLink>
        ))}
        <NavLink to="/app/analyst" data-testid="nav-analyst" className={navLinkClass}>
          <DollarSign className="w-4 h-4 stroke-[1.5]" />
          Refer & Earn
        </NavLink>
        {isAdmin && (
          <>
            <div className="pt-3 pb-1 px-3 font-mono text-[9px] uppercase tracking-widest text-zinc-700">DARA SYSTEMS</div>
            <NavLink to="/app/admin/leads" data-testid="nav-admin-leads" className={navLinkClass}>
              <Users className="w-4 h-4 stroke-[1.5]" />
              Leads
            </NavLink>
            <NavLink to="/app/admin/sales" data-testid="nav-admin-sales" className={navLinkClass}>
              <Briefcase className="w-4 h-4 stroke-[1.5]" />
              Sales Pipeline
            </NavLink>
            <NavLink to="/app/admin/funnel" data-testid="nav-admin-funnel" className={navLinkClass}>
              <BarChart3 className="w-4 h-4 stroke-[1.5]" />
              Funnel Analytics
            </NavLink>
            <NavLink to="/app/admin/outbound" data-testid="nav-admin-outbound" className={navLinkClass}>
              <MessageSquare className="w-4 h-4 stroke-[1.5]" />
              Outbound
            </NavLink>
            <NavLink to="/app/admin/analysts" data-testid="nav-admin-analysts" className={navLinkClass}>
              <Star className="w-4 h-4 stroke-[1.5]" />
              Expert Queue
            </NavLink>
            <NavLink to="/app/admin/case-studies" data-testid="nav-admin-case-studies" className={navLinkClass}>
              <Award className="w-4 h-4 stroke-[1.5]" />
              Case Studies
            </NavLink>
            <NavLink to="/app/admin/wall" data-testid="nav-admin-wall" className={navLinkClass}>
              <Sparkles className="w-4 h-4 stroke-[1.5]" />
              Pulse Wall
            </NavLink>
            <NavLink to="/app/admin/visitors" data-testid="nav-admin-visitors" className={navLinkClass}>
              <Globe className="w-4 h-4 stroke-[1.5]" />
              Visitors
            </NavLink>
            {!isSimple && (
              <>
                <NavLink to="/app/admin/calls" data-testid="nav-admin-calls" className={navLinkClass}>
                  <Inbox className="w-4 h-4 stroke-[1.5]" />
                  Sales Inbox
                </NavLink>
                <NavLink to="/app/admin/system" data-testid="nav-admin-system" className={navLinkClass}>
                  <Activity className="w-4 h-4 stroke-[1.5]" />
                  System
                </NavLink>
              </>
            )}
          </>
        )}
      </nav>
      <div className="p-3 border-t border-zinc-900">
        <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-700 leading-relaxed">
          DARA v2.1.0<br />
          DARA Engine · Live
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="dashboard-layout">
      {/* top bar */}
      <header className="border-b border-zinc-900 flex items-center">
        <div className="flex items-center gap-2 px-3 sm:px-4 py-3 sm:border-r sm:border-zinc-900 sm:w-[240px] flex-shrink-0">
          <button
            type="button"
            onClick={() => setNavOpen(o => !o)}
            data-testid="mobile-nav-toggle"
            className="lg:hidden text-zinc-400 hover:text-[#FFB000] -ml-1 mr-1 p-1"
            aria-label="Toggle navigation"
          >
            {navOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
          <Terminal className="w-4 h-4 text-[#FFB000]" />
          <span className="font-heading font-black tracking-tighter">DARA</span>
          <CircleDot className="w-2.5 h-2.5 text-[#00E676] pulse-dot ml-auto" />
        </div>
        <div className="flex-1 flex items-center justify-between px-3 sm:px-4 min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 truncate hidden sm:block min-w-0 flex-1 mr-3">
            WORKSPACE://{user?.company?.toUpperCase().replace(/\s+/g, "_")} · {user?.industry}
          </div>
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            <span className="font-mono text-[10px] text-zinc-600 hidden xl:inline">{now}</span>
            {/* Phase 36.12 — Trial countdown chip */}
            {trial && !trial.paid && trial.days_remaining !== null && !trial.is_super_admin && (
              <Link
                to="/app/settings?tab=billing"
                data-testid="trial-countdown-chip"
                title={trial.expired ? "Trial ended — upgrade to continue" : `${trial.days_remaining} days left in your trial · click to upgrade`}
                className={`hidden sm:flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 border transition-colors ${
                  trial.expired
                    ? "border-[#FF3B30]/60 text-[#FF3B30] hover:bg-[#FF3B30]/10"
                    : trial.days_remaining <= 3
                      ? "border-[#FF6B47]/60 text-[#FF6B47] hover:bg-[#FF6B47]/10"
                      : "border-[#FFB000]/40 text-[#FFB000] hover:bg-[#FFB000]/10"
                }`}
              >
                <Clock className="w-3 h-3" />
                {trial.expired
                  ? "TRIAL ENDED · UPGRADE"
                  : `${trial.days_remaining}D LEFT`}
              </Link>
            )}
            <button
              onClick={toggleMode}
              data-testid="mode-toggle-btn"
              className={`hidden sm:flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 border transition-colors ${
                isSimple
                  ? "border-[#00E676]/40 text-[#00E676] hover:bg-[#00E676]/10"
                  : "border-[#FFB000]/40 text-[#FFB000] hover:bg-[#FFB000]/10"
              }`}
              title={isSimple ? "Switch to full Terminal mode" : "Switch to Simple mode"}
            >
              {isSimple ? <Sparkles className="w-3 h-3" /> : <Zap className="w-3 h-3" />}
              {isSimple ? "SIMPLE" : "TERMINAL"}
            </button>
            <span className="font-mono text-[11px] text-zinc-300 truncate max-w-[100px] sm:max-w-none">{user?.name}</span>
            <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-zinc-800 text-[#FFB000] hidden sm:inline">
              {user?.role}
            </span>
            <button
              onClick={() => { logout(); nav("/"); }}
              data-testid="logout-btn"
              className="text-zinc-500 hover:text-[#FF3B30] transition-colors"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {!isSimple && <Ticker items={ticker} />}

      <div className="flex flex-1 min-h-0 relative">
        {/* desktop sidebar */}
        <aside className="hidden lg:flex w-[240px] border-r border-zinc-900 flex-col" data-testid="sidebar">
          {NavInner}
        </aside>

        {/* mobile drawer */}
        {navOpen && (
          <>
            <div
              className="fixed inset-0 bg-black/70 z-40 lg:hidden"
              onClick={() => setNavOpen(false)}
              aria-hidden="true"
            />
            <aside
              className="fixed top-0 left-0 bottom-0 w-[260px] max-w-[80vw] bg-black border-r border-zinc-900 flex flex-col z-50 lg:hidden"
              data-testid="sidebar-mobile"
            >
              <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-900">
                <Terminal className="w-4 h-4 text-[#FFB000]" />
                <span className="font-heading font-black tracking-tighter">DARA</span>
                <button
                  type="button"
                  onClick={() => setNavOpen(false)}
                  className="ml-auto text-zinc-500 hover:text-white p-1"
                  aria-label="Close navigation"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              {NavInner}
            </aside>
          </>
        )}

        {/* main */}
        <main className="flex-1 overflow-auto min-w-0" data-testid="main-content">
          <Outlet />
        </main>
      </div>
      <OnboardingWizard />
      {/* Phase 36.12 — Trial-expired blocking overlay (super-admin & paid bypass) */}
      {trial?.expired && (
        <div
          className="fixed inset-0 z-[60] bg-black/85 backdrop-blur flex items-center justify-center p-4"
          data-testid="trial-expired-overlay"
        >
          <div className="border border-[#FF3B30]/40 bg-zinc-950 max-w-lg w-full p-7">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-[#FF3B30]" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#FF3B30] font-bold">// TRIAL ENDED</span>
            </div>
            <h2 className="font-heading font-black text-2xl tracking-tight">
              Your 14-day DARA trial has ended.
            </h2>
            <p className="text-sm text-zinc-300 mt-3 leading-relaxed">
              Your workspace data is preserved. To resume access to insights, hedge ledger,
              cash-flow forecasts, and DARA-grounded playbooks — upgrade to Scale.
            </p>
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <Link
                to="/app/settings?tab=billing"
                data-testid="trial-expired-upgrade-btn"
                className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-2.5 hover:opacity-90 inline-flex items-center gap-2"
              >
                Upgrade to Scale →
              </Link>
              <button
                onClick={() => { logout(); nav("/"); }}
                className="border border-zinc-800 hover:border-zinc-700 text-zinc-400 hover:text-white font-mono text-[11px] uppercase tracking-widest px-4 py-2.5"
                data-testid="trial-expired-logout-btn"
              >
                Sign out
              </button>
            </div>
            <p className="font-mono text-[10px] text-zinc-600 mt-5 leading-relaxed">
              Need an extension or have questions? Email{" "}
              <a href="mailto:hello@daraos.ai" className="text-[#FFB000]">hello@daraos.ai</a>.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
